<template>
  <div class="h-full w-full min-h-0">
    <!-- Thumbnail Card View -->
    <div
      :class="[
        'relative h-full w-full cursor-pointer overflow-hidden group',
        !unstyled
          ? 'rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 hover:border-blue-500/50 transition-colors'
          : '',
      ]"
      @click="openModal">
      <div class="relative h-full w-full flex flex-col">
        <div class="flex-1 relative h-full overflow-hidden">
          <WebRTCPlayer
            v-if="showLivePreview"
            :camera-id="cameraId"
            :poster-src="snapshot"
            stream-preference="prefer-sub"
            :auto-connect="true"
            :muted="true"
            :controls="false"
            :show-status="false"
            :show-retry="false"
            :show-call="true"
            class="absolute inset-0 z-10 w-full h-full" />

          <!-- Disabled overlay -->
          <div v-if="isDisabled" class="flex flex-col items-center justify-center h-full">
            <span class="material-symbols-outlined text-3xl mb-1 text-gray-600">block</span>
            <p class="text-xs text-gray-500">{{ tt('Camera Disabled', 'Kamera onemogočena') }}</p>
          </div>

          <img v-else-if="snapshot" :src="snapshot" class="w-full h-full object-contain" />

          <div v-else class="flex flex-col items-center justify-center h-full text-gray-500">
            <span class="material-symbols-outlined text-4xl mb-2 opacity-50">videocam</span>
            <p class="text-sm">{{ tt('No Preview', 'Ni predogleda') }}</p>
          </div>

          <!-- Motion badge on thumbnail -->
          <div
            v-if="motionActive"
            class="absolute top-2 right-2 z-20 flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-600/90 border border-red-500/50 animate-pulse">
            <span class="material-symbols-outlined text-[10px] text-white">motion_sensor_active</span>
            <span class="text-[8px] font-bold tracking-wider text-white">{{ tt('MOTION', 'GIBANJE') }}</span>
          </div>
        </div>
      </div>

      <!-- Hover Overlay -->
      <div
        v-if="!hideControls"
        class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none flex items-center justify-center">
        <div
          class="bg-black/60 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm border border-white/10 transform translate-y-2 group-hover:translate-y-0 transition-transform">
          {{ tt('Click to Expand', 'Klikni za razširitev') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { useCameraModal } from '../composables/useCameraModal';
import WebRTCPlayer from './WebRTCPlayer.vue';
import { useSocketStore } from '../stores/socket';
import { useCamerasStore } from '../stores/cameras';
import { tt } from '../utils/consoleText';

const props = defineProps<{
  cameraId: number | string;
  cameraName?: string;
  snapshot?: string | null;
  isConnected?: boolean;
  hasPtz?: boolean;
  unstyled?: boolean;
  hideFooter?: boolean;
  hideControls?: boolean;
  privacyBlurActive?: boolean;
}>();

const modal = useCameraModal();
const socketStore = useSocketStore();
const camerasStore = useCamerasStore();

// Motion state
const MOTION_PREVIEW_TAIL_MS = 10_000;
const motionActive = ref(false);
const livePreviewActive = ref(false);
let motionCleanup: (() => void) | null = null;
let livePreviewStopTimer: ReturnType<typeof setTimeout> | null = null;

const isDisabled = computed(() => {
  const cam = camerasStore.getCamera(String(props.cameraId));
  return cam?.enabled === false;
});

const isModalOpenForCamera = computed(
  () => modal.isOpen.value && String(modal.current.value?.cameraId ?? '') === String(props.cameraId),
);

const showLivePreview = computed(
  () => livePreviewActive.value && !props.privacyBlurActive && !isModalOpenForCamera.value && !isDisabled.value,
);

function clearLivePreviewStopTimer() {
  if (!livePreviewStopTimer) return;
  clearTimeout(livePreviewStopTimer);
  livePreviewStopTimer = null;
}

function startLivePreview() {
  clearLivePreviewStopTimer();
  livePreviewActive.value = true;
}

function stopLivePreview() {
  clearLivePreviewStopTimer();
  livePreviewActive.value = false;
}

function scheduleLivePreviewStop() {
  clearLivePreviewStopTimer();
  livePreviewStopTimer = setTimeout(() => {
    livePreviewStopTimer = null;
    livePreviewActive.value = false;
  }, MOTION_PREVIEW_TAIL_MS);
}

function updateMotionState(active: boolean) {
  motionActive.value = active;

  if (active) {
    startLivePreview();
    return;
  }

  scheduleLivePreviewStop();
}

function openModal() {
  if (props.privacyBlurActive) return; // Block modal when privacy blur is active
  if (isDisabled.value) return; // Block modal when camera is disabled
  // Resolve PTZ from store or prop — use OR so either source can assert PTZ capability
  let hasPtz = props.hasPtz ?? false;
  let hasAudio = false;
  try {
    const storeCamera = camerasStore.getCamera(String(props.cameraId));
    if (storeCamera) {
      // OR with prop: stale store false must not silence a prop/local-data true
      hasPtz =
        !!(storeCamera.hasPtz || storeCamera.has_ptz || storeCamera.ptzServiceUrl || storeCamera.ptz_service_url) ||
        hasPtz;
      hasAudio = !!(storeCamera.hasAudio || storeCamera.has_audio);
    }
  } catch (e) {
    console.warn('[CameraWidget] Failed to resolve PTZ from store:', e);
  }

  modal.open({
    cameraId: props.cameraId,
    cameraName: props.cameraName || tt('Camera', 'Kamera'),
    hasPtz,
    hasAudio,
  });
}

// Listen for motion events on the thumbnail
function setupMotionListener() {
  // Clean up any previous listener
  motionCleanup?.();
  motionCleanup = null;

  if (!socketStore.socket) return;

  // Resolve deviceId from cameras store
  const storeCamera = camerasStore.getCamera(String(props.cameraId));
  const deviceId = storeCamera?.deviceId;
  if (!deviceId) return; // Store not loaded yet — will retry via watcher

  console.log(`[CameraWidget] Motion listener active: cameraId=${props.cameraId} deviceId=${deviceId}`);

  const handler = (payload: { deviceId?: string; valueKey?: string; value?: unknown }) => {
    if (payload.deviceId !== deviceId) return;
    if (payload.valueKey === 'motion_detected') {
      console.log(`[CameraWidget] Motion event for ${props.cameraId}: value=${payload.value}`);
      updateMotionState(payload.value === true || payload.value === 'true');
    }
  };

  socketStore.socket.on('device_value_changed', handler);
  motionCleanup = () => {
    socketStore.socket?.off('device_value_changed', handler);
  };
}

// Watch for cameras store to load (fixes race condition where widget mounts before store is populated)
watch(
  () => camerasStore.cameras,
  () => {
    if (!motionCleanup && camerasStore.getCamera(String(props.cameraId))?.deviceId) {
      setupMotionListener();
    }
  },
);

// Also watch for socket connection
watch(
  () => socketStore.connected,
  (connected) => {
    if (connected) {
      setupMotionListener();
    }
  },
);

watch(
  () => props.privacyBlurActive,
  (active) => {
    if (active) {
      motionActive.value = false;
      stopLivePreview();
    }
  },
);

onMounted(() => {
  setupMotionListener();
});

onUnmounted(() => {
  motionCleanup?.();
  clearLivePreviewStopTimer();
});
</script>

<style scoped>
.box-shadow-glow-green {
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.6);
}
</style>
